import { useState } from "react";
import { SaarthLogo } from "./Logo";

const NAV_LINKS = [
  { label: "How It Works", href: "#how-it-works" },
  { label: "Courses", href: "#courses" },
  { label: "About Us", href: "#about" },
  { label: "FAQ", href: "#faq" },
];

const COURSES = [
  {
    code: "DMLT",
    title: "Diploma in Medical Lab Technology",
    desc: "Master haematology, microbiology, biochemistry and clinical pathology with guided AI support.",
    icon: "🔬",
  },
  {
    code: "BMLT",
    title: "Bachelor in Medical Lab Technology",
    desc: "Deep-dive into lab sciences with concept maps, practice questions and personalised feedback.",
    icon: "🧪",
  },
  {
    code: "Nursing",
    title: "B.Sc Nursing / GNM",
    desc: "From anatomy to patient care — Prakash Sir breaks every topic into simple, memorable steps.",
    icon: "🩺",
  },
  {
    code: "BPharma",
    title: "B.Pharma / D.Pharma",
    desc: "Pharmacology, pharmaceutical chemistry and drug interactions explained in plain Hinglish.",
    icon: "💊",
  },
];

const FEATURES = [
  {
    icon: "🗺️",
    title: "Personalised Study Roadmap",
    desc: "Tell us your course and exam date. Prakash Sir builds a day-by-day plan tailored to you — no generic syllabi.",
  },
  {
    icon: "💬",
    title: "24 × 7 Doubt Solving",
    desc: "Ask anything, anytime. Get instant answers in simple Hinglish with ASCII mind maps and mnemonics.",
  },
  {
    icon: "📈",
    title: "Concept-to-Clinic Learning",
    desc: "Every concept is tied back to real clinical scenarios so you understand not just what, but why.",
  },
];

const STEPS = [
  { n: "01", title: "Create Your Free Account", desc: "Sign up in 30 seconds — pick your course and let Prakash Sir know where you are in your studies." },
  { n: "02", title: "Ask Anything", desc: "Type your doubt in plain English or Hinglish. Prakash Sir responds with a full breakdown, mind map and memory trick." },
  { n: "03", title: "Ace Your Exams", desc: "Practice daily, track your weak areas and walk into your exam fully prepared and confident." },
];

const FAQS = [
  { q: "Is SaarthAI free to use?", a: "Yes — SaarthAI is completely free during our early-access phase. We believe quality education should be accessible to every student regardless of their background." },
  { q: "Which courses does SaarthAI support?", a: "Currently we support DMLT, BMLT, B.Sc Nursing / GNM, and B.Pharma / D.Pharma. More courses are coming soon." },
  { q: "Why does Prakash Sir reply in Hinglish?", a: "Most of our students find concepts easier to grasp in a mix of Hindi and English — the way teachers actually speak in classrooms. You can also switch to pure Hindi, English, or Marathi during signup." },
  { q: "Can I use SaarthAI on my phone?", a: "Absolutely. The web app is fully mobile-responsive. Just open it in your phone browser — no app download needed." },
  { q: "How is this different from searching on Google or YouTube?", a: "Google gives you pages; SaarthAI gives you a conversation. Ask a follow-up, get a mind map, request a mnemonic — all in one chat with a tutor who knows your course." },
];

// ── ABOUT US content — edit this to update the About Us section ──────────────
const ABOUT_US = {
  eyebrow: "About Us",
  heading: "Making skill-based education affordable and accessible",
  vision: "We believe every student in India — regardless of where they live or how much they earn — deserves access to quality education. SaarthAI was built to make skill-based courses like DMLT, BMLT, Nursing and Pharmacy as easy to learn as having a knowledgeable friend by your side, 24 hours a day.",
  mission: "Our mission is simple: bridge the gap between ambition and opportunity for every student across India. No expensive coaching centres, no rigid schedules — just instant, personalised guidance in the language students actually think in.",
  values: [
    { icon: "🎯", label: "Affordable", desc: "Free for every student, always." },
    { icon: "🌍", label: "Accessible", desc: "Works on any device, anywhere in India." },
    { icon: "🤝", label: "Personalised", desc: "Tailored to your course, your pace, your language." },
  ],
};

export default function LandingPage({ onGetStarted, onLogin }) {
  const [openFaq, setOpenFaq] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="lp">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

        .lp *, .lp *::before, .lp *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .lp {
          font-family: 'Inter', sans-serif;
          color: #0D1F3C;
          background: #fff;
          --navy: #1B3A6B;
          --navy-dark: #0D1F3C;
          --navy-light: #2D5AA0;
          --navy-pale: #EEF3FF;
          --navy-glow: rgba(27,58,107,0.12);
          --border: #D4DCF0;
          --muted: #6B7FA3;
          --white: #fff;
        }

        /* ── NAV ── */
        .lp-nav {
          position: sticky; top: 0; z-index: 100;
          background: rgba(255,255,255,0.95);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--border);
          padding: 0 5%;
          display: flex; align-items: center; justify-content: space-between;
          height: 76px;
        }
        .lp-logo {
          display: flex; align-items: center; gap: 10px;
          text-decoration: none;
        }
        .lp-logo-text {
          font-size: 22px; font-weight: 800;
          color: var(--navy);
          letter-spacing: -0.5px;
          font-family: 'Baloo 2', cursive;
        }
        .lp-nav-links {
          display: flex; align-items: center; gap: 32px;
          list-style: none;
        }
        .lp-nav-links a {
          text-decoration: none;
          font-size: 15px; font-weight: 500;
          color: var(--muted);
          transition: color 0.2s;
        }
        .lp-nav-links a:hover { color: var(--navy); }
        .lp-nav-ctas { display: flex; align-items: center; gap: 12px; }
        .lp-btn-ghost {
          padding: 8px 18px;
          border: 1.5px solid var(--border);
          border-radius: 8px;
          font-size: 14px; font-weight: 600;
          color: var(--navy);
          background: transparent;
          cursor: pointer;
          transition: all 0.2s;
        }
        .lp-btn-ghost:hover { border-color: var(--navy); background: var(--navy-pale); }
        .lp-btn-primary {
          padding: 9px 20px;
          background: linear-gradient(135deg, var(--navy), var(--navy-light));
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px; font-weight: 600;
          cursor: pointer;
          transition: transform 0.15s, box-shadow 0.15s;
          box-shadow: 0 4px 14px var(--navy-glow);
        }
        .lp-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(27,58,107,0.25); }

        .lp-hamburger {
          display: none;
          background: none; border: none; cursor: pointer;
          font-size: 22px; color: var(--navy);
          padding: 4px;
        }
        .lp-mobile-menu {
          display: none;
          position: fixed; top: 68px; left: 0; right: 0;
          background: white;
          border-bottom: 1px solid var(--border);
          padding: 20px 5%;
          flex-direction: column; gap: 16px;
          z-index: 99;
          box-shadow: 0 8px 24px rgba(0,0,0,0.08);
        }
        .lp-mobile-menu.open { display: flex; }
        .lp-mobile-menu a {
          font-size: 16px; font-weight: 500;
          color: var(--navy-dark);
          text-decoration: none;
          padding: 8px 0;
          border-bottom: 1px solid var(--border);
        }

        @media (max-width: 768px) {
          .lp-nav-links, .lp-nav-ctas { display: none; }
          .lp-hamburger { display: block; }
        }

        /* ── HERO ── */
        .lp-hero {
          padding: 100px 5% 80px;
          text-align: center;
          background:
            radial-gradient(ellipse at 30% 0%, rgba(27,58,107,0.08) 0%, transparent 60%),
            radial-gradient(ellipse at 70% 100%, rgba(45,90,160,0.06) 0%, transparent 50%),
            #fff;
          position: relative;
          overflow: hidden;
        }
        .lp-hero-badge {
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--navy-pale);
          border: 1px solid rgba(27,58,107,0.2);
          border-radius: 100px;
          padding: 6px 16px;
          font-size: 13px; font-weight: 600;
          color: var(--navy);
          margin-bottom: 28px;
        }
        .lp-hero-badge span { font-size: 15px; }
        .lp-hero h1 {
          font-size: clamp(36px, 6vw, 68px);
          font-weight: 900;
          line-height: 1.1;
          letter-spacing: -2px;
          color: var(--navy-dark);
          max-width: 800px;
          margin: 0 auto 24px;
        }
        .lp-hero h1 em {
          font-style: normal;
          background: linear-gradient(135deg, var(--navy), var(--navy-light));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .lp-hero-sub {
          font-size: clamp(16px, 2vw, 19px);
          color: var(--muted);
          max-width: 560px;
          margin: 0 auto 40px;
          line-height: 1.65;
        }
        .lp-hero-actions {
          display: flex; align-items: center; justify-content: center;
          gap: 14px; flex-wrap: wrap;
        }
        .lp-hero-cta {
          padding: 15px 34px;
          background: linear-gradient(135deg, var(--navy), var(--navy-light));
          color: white; border: none; border-radius: 12px;
          font-size: 16px; font-weight: 700;
          cursor: pointer;
          box-shadow: 0 6px 24px rgba(27,58,107,0.3);
          transition: transform 0.15s, box-shadow 0.15s;
        }
        .lp-hero-cta:hover { transform: translateY(-2px); box-shadow: 0 10px 32px rgba(27,58,107,0.38); }
        .lp-hero-secondary {
          padding: 15px 28px;
          background: transparent;
          color: var(--navy);
          border: 2px solid var(--border);
          border-radius: 12px;
          font-size: 16px; font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .lp-hero-secondary:hover { border-color: var(--navy); background: var(--navy-pale); }
        .lp-hero-stats {
          display: flex; justify-content: center; align-items: center;
          gap: 48px; flex-wrap: wrap;
          margin-top: 60px;
          padding-top: 40px;
          border-top: 1px solid var(--border);
        }
        .lp-stat { text-align: center; }
        .lp-stat-num {
          font-size: 34px; font-weight: 900;
          color: var(--navy);
          letter-spacing: -1px;
        }
        .lp-stat-label { font-size: 13px; color: var(--muted); font-weight: 500; margin-top: 4px; }

        /* ── SECTION COMMON ── */
        .lp-section {
          padding: 80px 5%;
        }
        .lp-section-alt { background: #F8FAFF; }
        .lp-section-dark {
          background: var(--navy-dark);
          color: white;
        }
        .lp-section-header {
          text-align: center;
          max-width: 620px;
          margin: 0 auto 56px;
        }
        .lp-eyebrow {
          font-size: 12px; font-weight: 700;
          text-transform: uppercase; letter-spacing: 2px;
          color: var(--navy-light);
          margin-bottom: 12px;
        }
        .lp-section-dark .lp-eyebrow { color: #7AAEE8; }
        .lp-section-h2 {
          font-size: clamp(28px, 3.5vw, 42px);
          font-weight: 800;
          color: var(--navy-dark);
          letter-spacing: -1px;
          line-height: 1.2;
          margin-bottom: 16px;
        }
        .lp-section-dark .lp-section-h2 { color: white; }
        .lp-section-p {
          font-size: 16px; color: var(--muted);
          line-height: 1.7;
        }
        .lp-section-dark .lp-section-p { color: rgba(255,255,255,0.6); }

        /* ── FEATURES ── */
        .lp-features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 24px;
          max-width: 1100px;
          margin: 0 auto;
        }
        .lp-feature-card {
          background: white;
          border: 1px solid var(--border);
          border-radius: 20px;
          padding: 36px 32px;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .lp-feature-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 48px rgba(27,58,107,0.1);
        }
        .lp-feature-icon {
          font-size: 36px; margin-bottom: 20px;
          display: block;
        }
        .lp-feature-card h3 {
          font-size: 19px; font-weight: 700;
          color: var(--navy-dark);
          margin-bottom: 12px;
        }
        .lp-feature-card p { font-size: 15px; color: var(--muted); line-height: 1.7; }

        /* ── HOW IT WORKS ── */
        .lp-steps {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: 32px;
          max-width: 1100px;
          margin: 0 auto;
          position: relative;
        }
        .lp-step {
          text-align: center;
          padding: 40px 28px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 20px;
        }
        .lp-step-num {
          display: inline-flex; align-items: center; justify-content: center;
          width: 56px; height: 56px;
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 50%;
          font-size: 18px; font-weight: 800;
          color: white;
          margin-bottom: 20px;
        }
        .lp-step h3 { font-size: 18px; font-weight: 700; color: white; margin-bottom: 12px; }
        .lp-step p { font-size: 14px; color: rgba(255,255,255,0.6); line-height: 1.7; }

        /* ── COURSES ── */
        .lp-courses-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 20px;
          max-width: 1100px;
          margin: 0 auto;
        }
        .lp-course-card {
          background: white;
          border: 1.5px solid var(--border);
          border-radius: 18px;
          padding: 32px 28px;
          cursor: pointer;
          transition: all 0.2s;
          position: relative;
          overflow: hidden;
        }
        .lp-course-card::before {
          content: '';
          position: absolute; top: 0; left: 0; right: 0;
          height: 4px;
          background: linear-gradient(135deg, var(--navy), var(--navy-light));
          opacity: 0;
          transition: opacity 0.2s;
        }
        .lp-course-card:hover { border-color: var(--navy-light); transform: translateY(-4px); box-shadow: 0 16px 40px rgba(27,58,107,0.1); }
        .lp-course-card:hover::before { opacity: 1; }
        .lp-course-icon { font-size: 32px; margin-bottom: 16px; display: block; }
        .lp-course-code {
          display: inline-block;
          background: var(--navy-pale);
          color: var(--navy);
          font-size: 11px; font-weight: 700;
          letter-spacing: 1px; text-transform: uppercase;
          padding: 4px 10px; border-radius: 6px;
          margin-bottom: 12px;
        }
        .lp-course-card h3 { font-size: 16px; font-weight: 700; color: var(--navy-dark); margin-bottom: 10px; }
        .lp-course-card p { font-size: 14px; color: var(--muted); line-height: 1.6; }
        .lp-course-cta {
          display: inline-flex; align-items: center; gap: 6px;
          margin-top: 18px;
          font-size: 14px; font-weight: 600; color: var(--navy);
          text-decoration: none;
        }
        .lp-course-cta:hover { text-decoration: underline; }

        /* ── MEET PRAKASH SIR ── */
        .lp-meet-section { background: white; }
        .lp-meet-container {
          max-width: 1100px; margin: 0 auto;
          display: flex; align-items: center; gap: 60px;
          flex-wrap: wrap;
        }
        .lp-meet-image-wrap {
          flex: 0 0 auto;
        }
        .lp-meet-image {
          width: 280px; height: 320px;
          object-fit: cover; object-position: top;
          border-radius: 20px;
          box-shadow: 0 16px 48px rgba(27,58,107,0.18);
          border: 3px solid var(--navy-light);
        }
        .lp-meet-content { flex: 1; min-width: 280px; }
        .lp-meet-desc {
          font-size: 16px; color: var(--muted);
          line-height: 1.8; margin-bottom: 16px;
        }
        .lp-meet-langs {
          display: flex; gap: 12px; flex-wrap: wrap; margin-top: 24px;
        }
        .lp-meet-langs span {
          background: #EBF1FB;
          color: var(--navy);
          font-size: 13px; font-weight: 600;
          padding: 6px 14px; border-radius: 20px;
        }
        @media (max-width: 700px) {
          .lp-meet-container { flex-direction: column; gap: 32px; }
          .lp-meet-image { width: 200px; height: 230px; }
        }

        /* ── ABOUT US ── */
        .lp-about-body {
          max-width: 800px;
          margin: 0 auto;
        }
        .lp-about-text {
          font-size: 17px; color: var(--muted);
          line-height: 1.8; margin-bottom: 18px;
        }
        .lp-about-values {
          display: flex; gap: 20px; flex-wrap: wrap;
          margin-top: 40px;
        }
        .lp-about-value {
          flex: 1; min-width: 200px;
          background: white;
          border: 1.5px solid var(--border);
          border-radius: 16px;
          padding: 28px 24px;
          text-align: center;
          transition: box-shadow 0.2s;
        }
        .lp-about-value:hover { box-shadow: 0 8px 28px rgba(27,58,107,0.09); }
        .lp-about-value-icon { font-size: 32px; margin-bottom: 12px; display: block; }
        .lp-about-value h4 { font-size: 16px; font-weight: 700; color: var(--navy-dark); margin-bottom: 6px; }
        .lp-about-value p { font-size: 14px; color: var(--muted); }

        /* ── FAQ ── */
        .lp-faq-list {
          max-width: 720px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .lp-faq-item {
          border: 1.5px solid var(--border);
          border-radius: 14px;
          overflow: hidden;
          transition: border-color 0.2s;
        }
        .lp-faq-item.open { border-color: var(--navy-light); }
        .lp-faq-q {
          width: 100%;
          display: flex; justify-content: space-between; align-items: center;
          padding: 20px 24px;
          background: white;
          border: none; cursor: pointer;
          font-size: 16px; font-weight: 600;
          color: var(--navy-dark);
          text-align: left;
          gap: 16px;
        }
        .lp-faq-item.open .lp-faq-q { color: var(--navy); }
        .lp-faq-icon {
          font-size: 20px; color: var(--muted);
          flex-shrink: 0;
          transition: transform 0.25s;
        }
        .lp-faq-item.open .lp-faq-icon { transform: rotate(45deg); color: var(--navy); }
        .lp-faq-a {
          max-height: 0; overflow: hidden;
          transition: max-height 0.3s ease, padding 0.3s;
          background: #F8FAFF;
          padding: 0 24px;
          font-size: 15px; color: var(--muted); line-height: 1.7;
        }
        .lp-faq-item.open .lp-faq-a {
          max-height: 200px;
          padding: 0 24px 20px;
        }

        /* ── CTA BANNER ── */
        .lp-cta-banner {
          background: linear-gradient(135deg, var(--navy-dark) 0%, var(--navy) 60%, var(--navy-light) 100%);
          padding: 80px 5%;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .lp-cta-banner::before {
          content: '';
          position: absolute; inset: 0;
          background: radial-gradient(ellipse at 50% 50%, rgba(255,255,255,0.05) 0%, transparent 70%);
        }
        .lp-cta-banner h2 {
          font-size: clamp(28px, 4vw, 48px);
          font-weight: 900; color: white;
          letter-spacing: -1px; margin-bottom: 16px;
          position: relative;
        }
        .lp-cta-banner p {
          font-size: 18px; color: rgba(255,255,255,0.7);
          margin-bottom: 36px; position: relative;
        }
        .lp-cta-banner-btn {
          padding: 16px 40px;
          background: white;
          color: var(--navy-dark);
          border: none; border-radius: 12px;
          font-size: 17px; font-weight: 700;
          cursor: pointer;
          box-shadow: 0 8px 32px rgba(0,0,0,0.2);
          transition: transform 0.15s;
          position: relative;
        }
        .lp-cta-banner-btn:hover { transform: translateY(-2px); }

        /* ── FOOTER ── */
        .lp-footer {
          background: var(--navy-dark);
          padding: 48px 5% 32px;
          color: rgba(255,255,255,0.5);
        }
        .lp-footer-top {
          display: flex; justify-content: space-between; align-items: flex-start;
          flex-wrap: wrap; gap: 32px;
          padding-bottom: 40px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
          margin-bottom: 32px;
        }
        .lp-footer-brand .lp-logo-text { color: white; font-size: 22px; }
        .lp-footer-brand p { font-size: 14px; margin-top: 10px; max-width: 260px; line-height: 1.6; }
        .lp-footer-col h4 { font-size: 13px; font-weight: 700; color: white; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 14px; }
        .lp-footer-col ul { list-style: none; display: flex; flex-direction: column; gap: 10px; }
        .lp-footer-col ul li a {
          font-size: 14px; color: rgba(255,255,255,0.5);
          text-decoration: none; transition: color 0.2s;
        }
        .lp-footer-col ul li a:hover { color: white; }
        .lp-footer-bottom {
          display: flex; justify-content: space-between; align-items: center;
          flex-wrap: wrap; gap: 12px;
          font-size: 13px;
        }
        .lp-footer-bottom a { color: rgba(255,255,255,0.4); text-decoration: none; }
        .lp-footer-bottom a:hover { color: white; }

        @media (max-width: 600px) {
          .lp-hero { padding: 70px 5% 60px; }
          .lp-hero-stats { gap: 28px; }
          .lp-footer-top { flex-direction: column; }
        }
      `}</style>

      {/* NAV */}
      <nav className="lp-nav">
        <a className="lp-logo" href="#top">
          <SaarthLogo height={38} />
          <span className="lp-logo-text">SaarthAI</span>
        </a>
        <ul className="lp-nav-links">
          {NAV_LINKS.map(l => (
            <li key={l.label}><a href={l.href}>{l.label}</a></li>
          ))}
        </ul>
        <div className="lp-nav-ctas">
          <button className="lp-btn-ghost" onClick={onLogin}>Login</button>
          <button className="lp-btn-primary" onClick={onGetStarted}>Get Started Free</button>
        </div>
        <button className="lp-hamburger" onClick={() => setMenuOpen(o => !o)}>
          {menuOpen ? "✕" : "☰"}
        </button>
      </nav>

      {/* MOBILE MENU */}
      <div className={`lp-mobile-menu ${menuOpen ? "open" : ""}`}>
        {NAV_LINKS.map(l => (
          <a key={l.label} href={l.href} onClick={() => setMenuOpen(false)}>{l.label}</a>
        ))}
        <button className="lp-btn-ghost" onClick={onLogin} style={{ width: "100%" }}>Login</button>
        <button className="lp-btn-primary" onClick={onGetStarted} style={{ width: "100%" }}>Get Started Free</button>
      </div>

      {/* HERO */}
      <section className="lp-hero" id="top">
        <div className="lp-hero-badge"><span>🎓</span> Free for all students — no credit card needed</div>
        <h1>
          Your Affordable<br />
          <em>1:1 AI Study Partner</em><br />
          for Skill-Based Courses
        </h1>
        <p className="lp-hero-sub">
          SaarthAI helps DMLT, BMLT, Nursing and Pharma students in India master their courses — with instant doubt-solving, mind maps and mnemonics, available 24×7 in simple Hinglish.
        </p>
        <div className="lp-hero-actions">
          <button className="lp-hero-cta" onClick={onGetStarted}>Start Learning Free →</button>
          <button className="lp-hero-secondary" onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}>See How It Works</button>
        </div>
        <div className="lp-hero-stats">
          <div className="lp-stat">
            <div className="lp-stat-num">4+</div>
            <div className="lp-stat-label">Courses Supported</div>
          </div>
          <div className="lp-stat">
            <div className="lp-stat-num">24×7</div>
            <div className="lp-stat-label">Always Available</div>
          </div>
          <div className="lp-stat">
            <div className="lp-stat-num">100%</div>
            <div className="lp-stat-label">Free to Use</div>
          </div>
          <div className="lp-stat">
            <div className="lp-stat-num">Hinglish</div>
            <div className="lp-stat-label">Plain Language Answers</div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="lp-section lp-section-alt">
        <div className="lp-section-header">
          <div className="lp-eyebrow">Why SaarthAI</div>
          <h2 className="lp-section-h2">Everything a student needs to succeed</h2>
          <p className="lp-section-p">Built specifically for allied health and pharma students across every corner of India.</p>
        </div>
        <div className="lp-features-grid">
          {FEATURES.map(f => (
            <div className="lp-feature-card" key={f.title}>
              <span className="lp-feature-icon">{f.icon}</span>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="lp-section lp-section-dark" id="how-it-works">
        <div className="lp-section-header">
          <div className="lp-eyebrow">How It Works</div>
          <h2 className="lp-section-h2">From sign-up to exam-ready in 3 steps</h2>
          <p className="lp-section-p">No complex setup. No confusing interface. Just open the app and start learning.</p>
        </div>
        <div className="lp-steps">
          {STEPS.map(s => (
            <div className="lp-step" key={s.n}>
              <div className="lp-step-num">{s.n}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* COURSES */}
      <section className="lp-section" id="courses">
        <div className="lp-section-header">
          <div className="lp-eyebrow">Supported Courses</div>
          <h2 className="lp-section-h2">Built for your course, not a generic syllabus</h2>
          <p className="lp-section-p">Prakash Sir knows your curriculum inside-out and tailors every explanation to your specific course.</p>
        </div>
        <div className="lp-courses-grid">
          {COURSES.map(c => (
            <div className="lp-course-card" key={c.code} onClick={onGetStarted}>
              <span className="lp-course-icon">{c.icon}</span>
              <span className="lp-course-code">{c.code}</span>
              <h3>{c.title}</h3>
              <p>{c.desc}</p>
              <span className="lp-course-cta">Start Learning →</span>
            </div>
          ))}
        </div>
      </section>

      {/* MEET PRAKASH SIR */}
      <section className="lp-section lp-meet-section">
        <div className="lp-meet-container">
          <div className="lp-meet-image-wrap">
            <img src="/PrakashSir.jpg" alt="Prakash Sir" className="lp-meet-image" />
          </div>
          <div className="lp-meet-content">
            <div className="lp-eyebrow">Meet Your Mentor</div>
            <h2 className="lp-section-h2" style={{ textAlign: "left", marginBottom: 16 }}>Prakash Sir</h2>
            <p className="lp-meet-desc">
              Prakash Sir is your always-available AI study companion — built specifically for DMLT, BMLT, Nursing, and BPharma students. He explains every concept in your preferred language, creates personalised study plans, and never lets you feel lost or alone in your preparation.
            </p>
            <p className="lp-meet-desc">
              Whether it's 2 AM before an exam or a lazy Sunday afternoon, Prakash Sir is there — with ASCII mind maps, easy mnemonics, and the warmth of a teacher who genuinely believes in you.
            </p>
            <div className="lp-meet-langs">
              <span>🇮🇳 Hinglish</span>
              <span>हिं Hindi</span>
              <span>🇬🇧 English</span>
              <span>🟠 मराठी</span>
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT US */}
      <section className="lp-section lp-section-alt" id="about">
        <div className="lp-section-header">
          <div className="lp-eyebrow">{ABOUT_US.eyebrow}</div>
          <h2 className="lp-section-h2">{ABOUT_US.heading}</h2>
        </div>
        <div className="lp-about-body">
          <p className="lp-about-text">{ABOUT_US.vision}</p>
          <p className="lp-about-text">{ABOUT_US.mission}</p>
          <div className="lp-about-values">
            {ABOUT_US.values.map(v => (
              <div className="lp-about-value" key={v.label}>
                <span className="lp-about-value-icon">{v.icon}</span>
                <h4>{v.label}</h4>
                <p>{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="lp-section" id="faq">
        <div className="lp-section-header">
          <div className="lp-eyebrow">FAQ</div>
          <h2 className="lp-section-h2">Questions? We've got answers.</h2>
          <p className="lp-section-p">Everything you need to know before you start.</p>
        </div>
        <div className="lp-faq-list">
          {FAQS.map((f, i) => (
            <div className={`lp-faq-item ${openFaq === i ? "open" : ""}`} key={i}>
              <button className="lp-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                {f.q}
                <span className="lp-faq-icon">+</span>
              </button>
              <div className="lp-faq-a">{f.a}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA BANNER */}
      <div className="lp-cta-banner">
        <h2>Ready to ace your exams?</h2>
        <p>Join thousands of students getting smarter every day with SaarthAI.</p>
        <button className="lp-cta-banner-btn" onClick={onGetStarted}>Get Started — It's Free</button>
      </div>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="lp-footer-top">
          <div className="lp-footer-brand">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <SaarthLogo height={36} variant="mono" />
              <span className="lp-logo-text" style={{ color:"white" }}>SaarthAI</span>
            </div>
            <p>Your 24×7 AI study partner for skill-based medical and pharma courses in India.</p>
          </div>
          <div className="lp-footer-col">
            <h4>Product</h4>
            <ul>
              <li><a href="#how-it-works">How It Works</a></li>
              <li><a href="#courses">Courses</a></li>
              <li><a href="#faq">FAQ</a></li>
            </ul>
          </div>
          <div className="lp-footer-col">
            <h4>Company</h4>
            <ul>
              <li><a href="#about">About Us</a></li>
            </ul>
          </div>
          <div className="lp-footer-col">
            <h4>Legal</h4>
            <ul>
              <li><a href="#top">Privacy Policy</a></li>
              <li><a href="#top">Terms of Use</a></li>
            </ul>
          </div>
        </div>
        <div className="lp-footer-bottom">
          <span>© {new Date().getFullYear()} SaarthAI. All rights reserved.</span>
          <span>Made with ❤️ for students across India</span>
        </div>
      </footer>
    </div>
  );
}
